import React, { useContext, useEffect, useRef, useState } from "react";
import { Project } from "utils/interfaces";
import {
  MdArrowForward,
  MdBolt,
  MdCheck,
  MdMoreHoriz,
  MdStackedBarChart,
  MdTerminal,
} from "react-icons/md";
import { GoAlertFill } from "react-icons/go";
import processEstTime from "../functions/processEstTime";
import { checkResultComplete } from "services/resultsRequests";
import { MainContext } from "components/utils/Contexts";
import useMouseClickOutside from "hooks/MouseClickOutside";
import EditDeleteMenu from "../components/EditDeleteMenu";
import ImageSkeleton from "utils/skeletons/ImageSkeleton";

export interface ProjectCardProps {
  project: Project;
  handleExpand?: () => void;
  handleEdit: (event: React.MouseEvent) => void;
  handleDelete: (event: React.MouseEvent) => void;
  handleAddToClasses: (event: React.MouseEvent) => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  project,
  handleEdit,
  handleDelete,
  handleExpand,
  handleAddToClasses,
}) => {
  // States definition
  const activateEditButton = project.canEdit || false;
  const [showOptionsMenu, setShowOptionsMenu] = useState(false);
  const [isComplete, setIsComplete] = useState();

  // Create a menu ref
  const menuRef: React.RefObject<HTMLDivElement> = useRef(null);

  // Get context parameters
  const { userRole } = useContext(MainContext);

  const updateComplete = async () => {
    if (project._id && ["student", "parent"].includes(userRole)) {
      setIsComplete(await checkResultComplete("projects", project._id));
    }
  };
  useEffect(() => {
    updateComplete();
  }, [project._id]);

  /**
   * Hook used when clicking outside the dropdown menu
   */
  useMouseClickOutside(
    menuRef,
    () => setShowOptionsMenu(false),
    showOptionsMenu,
  );

  /**
   * Define a deactivated function in order to prevent to execute the
   * function that underlies a button
   * @param event
   */
  const deactivatedFunction = (event: React.MouseEvent) => {
    event.stopPropagation();
  };

  /**
   * Function used to open the pop up menu with the edit and
   * delete option
   * @param event
   */
  const handleClickOptions = (event: React.MouseEvent) => {
    // First of all, prevent clicking the card
    event.stopPropagation();

    // Toggle the state
    setShowOptionsMenu(!showOptionsMenu);
  };

  /**
   * Function used to wrap the edit or delete functions
   * in order to add custom functionalities per card
   */
  const handleClickOptionWrapper = (
    func: (event: React.MouseEvent) => void,
  ) => {
    return (event: React.MouseEvent) => {
      // Close the menu when clicking on one of the options
      setShowOptionsMenu(false);

      // And execute the function
      func(event);
    };
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`w-full h-[133px] px-6 py-4 bg-neutral-25 rounded-xl justify-between items-center inline-flex gap-6 cursor-pointer hover:shadow-card ease-in-out duration-150 relative ${
        isComplete
          ? "border-2 border-success-500"
          : project.profanityFlag
            ? "border-2 border-warning-500"
            : ""
      }`}
      onClick={handleExpand}
    >
      {/* Complete sign */}
      {isComplete && (
        <div className="absolute left-0 top-0 w-8 h-8">
          <div className="relative w-full h-full p-1 rounded-tl-lg rounded-br-xl bg-success-500">
            <MdCheck className="w-full h-full text-white" />
          </div>
        </div>
      )}
      {/* Profanity flag sign */}
      {project.profanityFlag && (
        <div className="absolute left-0 top-0 size-8 z-20">
          <div
            className="relative w-full h-full p-1 rounded-tl-lg rounded-br-xl bg-warning-500 inline-flex items-center justify-center"
            title="This project has content flagged as profane"
          >
            <GoAlertFill className="size-5 text-white" />
          </div>
        </div>
      )}
      <div className="w-[270px] h-[97px] justify-start items-center gap-2 flex">
        <ImageSkeleton
          src={project.headerImage}
          alt={project.title}
          className="w-[68px] h-[68px] rounded-full relative"
        />
        <div className="grow shrink basis-0 flex-col justify-center items-start gap-1 inline-flex">
          <div className="max-h-10 w-full text-neutral-700 text-base font-semibold font-sans leading-[19px] line-clamp-2">
            {project.title || "-"}
          </div>
          <div className="w-full text-neutral-600 text-xs font-normal font-sans leading-[18px] line-clamp-3">
            {project.description || "-"}
          </div>
        </div>
      </div>
      <div className="justify-start items-center gap-1 flex">
        <div className="p-1 rounded-full justify-start items-end gap-2 flex">
          <MdStackedBarChart className="w-8 h-8 fill-neutral-700" />
        </div>
        <div className="flex-col justify-center items-center gap-1 inline-flex">
          <div className="flex-col justify-center items-center gap-1 flex">
            <div className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-[19px]">
              {project.level || "-"}
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-start text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Program Level
                </div>
              </div>
            </div>
          </div>
          {/* <div className="self-stretch h-0.5 bg-neutral-200 flex-col justify-start items-start gap-2 flex">
            <div className="w-[21px] h-0.5 bg-success-500" />
          </div> */}
        </div>
      </div>
      <div className="justify-start items-center gap-1 flex">
        <div className="p-1 rounded-full justify-start items-end gap-2 flex">
          <MdBolt className="w-8 h-8 fill-neutral-700" />
        </div>
        <div className="flex-col justify-center items-center gap-1 inline-flex">
          <div className="flex-col justify-center items-center gap-1 flex">
            <div className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-[19px]">
              {project.experiencePoints || 0}
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-start text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Knowledge points
                </div>
              </div>
            </div>
          </div>
          {/* <div className="self-stretch h-0.5 bg-neutral-200 flex-col justify-start items-start gap-2 flex">
            <div className="w-[21px] h-0.5 bg-success-500" />
          </div> */}
        </div>
      </div>
      <div className="justify-start items-center gap-1 flex">
        <div className="p-1 rounded-full justify-start items-end gap-2 flex">
          <MdTerminal className="w-8 h-8 fill-neutral-700" />
        </div>
        <div className="flex-col justify-start items-center gap-1 inline-flex">
          <div className="flex-col justify-center items-center gap-1 flex">
            <div className="self-stretch text-neutral-700 text-base font-semibold font-sans leading-[19px]">
              {project.estimatedTime
                ? processEstTime(project.estimatedTime)
                : 0}
            </div>
            <div className="pb-[3px] flex-col justify-center items-end gap-2 flex">
              <div className="justify-center items-end gap-2 inline-flex">
                <div className="text-start text-neutral-500 text-[10px] font-normal font-sans uppercase leading-3 tracking-wide">
                  Estimated time (in mins)
                </div>
              </div>
            </div>
          </div>
          {/* <div className="self-stretch h-0.5 bg-neutral-200 flex-col justify-start items-start gap-2 flex">
            <div className="w-[21px] h-0.5 bg-success-500" />
          </div> */}
        </div>
      </div>
      <div className="w-fit self-stretch justify-center items-center gap-3 flex">
        {/* <div
            className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer"
            onClick={handleAddToClasses}
          >
            <div className="w-6 h-6">
              <MdOutlineAddToPhotos className="w-full h-full fill-neutral-700" />
            </div>
          </div> */}
        {["customer admin", "master admin", "class admin"].includes(
          userRole,
        ) && (
          <div
            className="w-12 self-stretch justify-center items-center gap-3 inline-flex relative"
            ref={menuRef}
          >
            <button
              className={`p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex z-20 ${
                activateEditButton
                  ? "hover:bg-neutral-200 ease-in-out duration-300 cursor-pointer"
                  : "cursor-not-allowed"
              }`}
              onClick={
                activateEditButton ? handleClickOptions : deactivatedFunction
              }
              title={
                activateEditButton
                  ? "More options"
                  : "This user is not allowed to edit this project"
              }
            >
              <MdMoreHoriz
                className={`w-6 h-6 ${
                  activateEditButton ? "fill-neutral-700" : "fill-neutral-200"
                }`}
              />
            </button>
            <EditDeleteMenu
              show={showOptionsMenu}
              handleEdit={handleClickOptionWrapper(handleEdit)}
              handleDelete={handleClickOptionWrapper(handleDelete)}
              containerStyle="absolute w-fit top-[78px] right-0 p-3 bg-white rounded-lg shadow flex-col justify-center items-center gap-2 inline-flex z-30"
            />
          </div>
        )}
        {["customer admin", "class admin", "parent", "student"].includes(
          userRole,
        ) && (
          <button
            className="p-3 bg-neutral-100 rounded-full justify-start items-start gap-2 flex cursor-pointer hover:bg-neutral-200 ease-in-out duration-300"
            onClick={() => {}}
            title="Continue"
          >
            <MdArrowForward className="w-6 h-6 fill-neutral-700" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ProjectCard;
